import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Queue } from "@/types/queue";
import { QueueDetailCard } from "@/components/queue-detail/QueueDetailCard";
import { QRCodeDialog } from "@/components/queue-detail/QRCodeDialog";

const QueueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [queue, setQueue] = useState<Queue | null>(null);
  const [showQR, setShowQR] = useState(false);
  const { toast } = useToast();

  const fetchQueue = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      const { data, error } = await supabase
        .from('queues')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      const { count } = await supabase
        .from('queue_participants')
        .select('*', { count: 'exact', head: true })
        .eq('queue_id', id)
        .eq('status', 'waiting');

      setQueue({
        ...data,
        people_in_queue: count || 0
      });
    } catch (error: any) {
      toast({
        title: "Error fetching queue",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchQueue();

    const channel = supabase
      .channel('queue-detail-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'queue_participants',
          filter: `queue_id=eq.${id}`
        },
        () => fetchQueue()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'queues',
          filter: `id=eq.${id}`
        },
        () => fetchQueue()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  const handleNext = async () => {
    if (!queue || !queue.is_active) return;

    try {
      const { data: participants, error: fetchError } = await supabase
        .from('queue_participants')
        .select('*')
        .eq('queue_id', queue.id)
        .eq('status', 'waiting')
        .order('joined_at', { ascending: true })
        .limit(1);

      if (fetchError) throw fetchError;

      if (participants && participants.length > 0) {
        const nextParticipant = participants[0];

        const { error: updateError } = await supabase
          .from('queue_participants')
          .update({
            status: 'served',
            served_at: new Date().toISOString()
          })
          .eq('id', nextParticipant.id);

        if (updateError) throw updateError;

        const { error: queueError } = await supabase
          .from('queues')
          .update({
            current_number: nextParticipant.ticket_number,
            last_updated: new Date().toISOString()
          })
          .eq('id', queue.id);

        if (queueError) throw queueError;

        toast({
          title: "Next person called",
          description: `Ticket number ${nextParticipant.ticket_number} has been called.`,
        });

        await fetchQueue();
      } else {
        toast({
          title: "No one in line",
          description: "There are no more people waiting in the queue.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error processing next in line",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getQRCodeValue = () => {
    if (!queue) return "";
    return `https://join.qninja.dev?uid=${queue.id}`;
  };

  return (
    <div className="min-h-screen bg-secondary/30 py-8">
      <div className="container px-4 space-y-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/dashboard")} 
          className="hover:bg-accent/10 hover:text-accent"
        >
          <ArrowLeft className="mr-2" size={16} />
          Back to Queues
        </Button>

        {queue && (
          <QueueDetailCard
            queue={queue}
            onNext={handleNext}
            onShowQR={() => queue.is_active && setShowQR(true)}
          />
        )}

        <QRCodeDialog
          open={showQR}
          onOpenChange={setShowQR}
          qrValue={getQRCodeValue()}
        />
      </div>
    </div>
  );
};

export default QueueDetail;
